/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import {
  ClientConnectionDataArray,
  DBSupplierConnectionResponse,
} from '@tyrio/dto';
import * as _ from 'lodash';
import {
  FieldValues,
  RegisterOptions,
  UseFormClearErrors,
} from 'react-hook-form';
import { ValidationProps } from '../../supplier-form/helpers/types';
import { TextFieldInput } from '../../supplier-form/helpers/components';

interface Props {
  c: DBSupplierConnectionResponse;
  connectionData: ClientConnectionDataArray;
  setConnectionData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  getValidationProps: (
    key: string,
    validationRules?: RegisterOptions,
    validationText?: string
  ) => ValidationProps;
  clearErrors: UseFormClearErrors<FieldValues>;
}
export const ClientConnectionEmailConfiguration = ({
  c,
  connectionData,
  setConnectionData,
  getValidationProps,
  clearErrors,
}: Props) => {
  const rootItem = useMemo(() => {
    return connectionData.find((item) => item.adminConnectionId === c.id);
  }, [c.id, connectionData]);
  const idx = useMemo(() => {
    const caughtIdx = connectionData.findIndex(
      (item) => item.adminConnectionId === c.id
    );
    if (caughtIdx === -1) return connectionData.length;
    return caughtIdx;
  }, [c.id, connectionData]);

  const [itemValue, setItemValueState] = useState(
    Object.assign(
      {
        type: 'EMAIL',
        adminConnectionId: c.id,
        connectionType: c.connectionTypes,
        connectionDetails: {
          supplierEmail: '',
          remark: '',
        },
      },
      rootItem
    )
  );

  const setItemValue = useCallback(
    (data: any) => {
      const copied = _.cloneDeep(connectionData);
      copied[idx] = data;
      setItemValueState(data);
      setConnectionData(copied);
    },
    [connectionData, idx, setConnectionData]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={6}>
        <Autocomplete
          multiple
          id={c.id + '-Connection type'}
          value={c.connectionTypes}
          renderInput={(params) => (
            <TextField {...params} label="Connection type" />
          )}
          renderTags={(tagValue: string[], getTagProps) =>
            c.connectionTypes.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} disabled />
            ))
          }
          disabled
          options={c.connectionTypes}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <FormControl fullWidth>
          <InputLabel id={c.id + '-protocol-label'}>
            Connection protocol
          </InputLabel>
          <Select
            labelId={c.id + '-protocol-label'}
            id={c.id + '-protocol'}
            value={c.connectionProtocol}
            label="Connection protocol"
            onChange={() => null}
            disabled
          >
            <MenuItem value={c.connectionProtocol}>
              {c.connectionProtocol}
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={12}>
        <TextFieldInput
          value={itemValue?.connectionDetails.supplierEmail}
          onChange={(e) => {
            setItemValue({
              ...itemValue,
              connectionDetails: {
                ..._.get(itemValue, `connectionDetails`, {}),
                supplierEmail: e.target.value,
              },
            });
            clearErrors(
              `connectionDetails-emailConnection-supplierEmail-${c.emailConnection?.id}`
            );
          }}
          label="Supplier Email"
          validationProps={{
            ...getValidationProps(
              `connectionDetails-emailConnection-supplierEmail-${c.emailConnection?.id}`
            ),
          }}
        />
      </Grid>

      <Grid item xs={12} lg={12}>
        <TextField
          fullWidth
          value={itemValue?.connectionDetails.remark}
          onChange={(e) =>
            setItemValue({
              ...itemValue,
              connectionDetails: {
                ..._.get(itemValue, `connectionDetails`, {}),
                remark: e.target.value,
              },
            })
          }
          id="remark"
          label="Remark"
          variant="outlined"
        />
      </Grid>
    </Grid>
  );
};
